import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mountain, TreePine } from "lucide-react";
import { login, register } from "@/lib/auth";
import { toast } from "sonner";
import { SsoButtons } from "@/components/SsoButtons";
import { useSearchParams } from "react-router-dom";
export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ssoErrorParam = searchParams.get("sso_error");
  const ssoEmail = searchParams.get("email");
  const [loginEmail, setLoginEmail] = useState(ssoEmail ?? "demo@hollerhub.com");
  const [loginPassword, setLoginPassword] = useState("demo123");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regRanch, setRegRanch] = useState("");
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState("");
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err: any) {
      setLoginError(err?.message || "Login failed");
      console.error("[Login]", err);
    } finally {
      setLoginLoading(false);
    }
  };
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError("");
    if (!regName || !regEmail || !regPassword || !regRanch) {
      setRegError("All fields are required");
      return;
    }
    setRegLoading(true);
    try {
      await register(regName, regEmail, regPassword, regRanch);
      toast.success("Ranch created! Welcome!");
      navigate("/");
    } catch (err: any) {
      setRegError(err?.message || "Registration failed");
      console.error("[Register]", err);
    } finally {
      setRegLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex">
      {/* Left decorative panel — Forest hero with reversed lockup */}
      <div className="hidden lg:flex lg:w-1/2 bg-forest dark:bg-forest-dark relative overflow-hidden">
        {/* Subtle Burnt-Orange wash bottom-right for warmth without competing with Forest */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-burnt-orange/20" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-sand">
          {/* Reversed E2 mark + wordmark, Sand on Forest */}
          <div className="flex items-center gap-4 mb-10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 64 64"
              className="h-14 w-14"
              role="img"
              aria-label="HollerHub"
            >
              <rect x="1.75" y="1.75" width="60.5" height="60.5" rx="6" ry="6"
                    fill="none" stroke="#F2EDE4" strokeWidth="3.5" />
              <rect x="9.25" y="9.25" width="45.5" height="45.5" rx="3" ry="3"
                    fill="none" stroke="#D4845F" strokeOpacity="0.55" strokeWidth="1.5" />
              <g fill="#F2EDE4">
                <rect x="18.5" y="18" width="6" height="28" />
                <rect x="39.5" y="18" width="6" height="28" />
                <rect x="18.5" y="29" width="27" height="6" />
              </g>
            </svg>
            <h1 className="font-display text-4xl">
              Holler<span className="italic text-clay-dark">Hub</span>
            </h1>
          </div>

          {/* Display headline — DM Serif Display per brand guidelines */}
          <h2 className="font-display text-5xl mb-5 leading-tight">
            The land doesn&apos;t wait.<br />
            <span className="italic text-clay-dark">Neither do we.</span>
          </h2>
          <p className="text-lg text-sand/80 max-w-md leading-relaxed">
            Built for working ranches. Track your herd, manage documents,
            schedule maintenance — everything you need, nothing you don&apos;t.
          </p>
          <div className="flex gap-6 mt-12 text-sand/60 font-mono text-xs uppercase tracking-wider">
            <div className="flex items-center gap-2">
              <Mountain className="h-4 w-4" />
              <span>Livestock &amp; equipment</span>
            </div>
            <div className="flex items-center gap-2">
              <TreePine className="h-4 w-4" />
              <span>Land &amp; asset management</span>
            </div>
          </div>
        </div>
      </div>
      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo — full lockup */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 64 64"
              className="h-10 w-10"
              role="img"
              aria-label="HollerHub"
            >
              <rect x="1.75" y="1.75" width="60.5" height="60.5" rx="6" ry="6"
                    fill="none" stroke="#2D4A3E" strokeWidth="3.5" />
              <rect x="9.25" y="9.25" width="45.5" height="45.5" rx="3" ry="3"
                    fill="none" stroke="#B5603E" strokeOpacity="0.35" strokeWidth="1.5" />
              <g fill="#2D4A3E">
                <rect x="18.5" y="18" width="6" height="28" />
                <rect x="39.5" y="18" width="6" height="28" />
                <rect x="18.5" y="29" width="27" height="6" />
              </g>
            </svg>
            <h1 className="font-display text-2xl text-forest dark:text-forest-dark">
              Holler<span className="italic text-clay dark:text-clay-dark">Hub</span>
            </h1>
          </div>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Sign In</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle>Welcome Back</CardTitle>
                  <CardDescription>
                    Sign in to your ranch. Demo credentials are pre-filled.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="demo@hollerhub.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••"
                      />
                    </div>
                    {loginError && (
                      <p className="text-sm text-destructive">{loginError}</p>
                    )}
                    {ssoErrorParam === "no_account" && (
                      <p className="text-sm text-destructive">
                        No HollerHub account is associated with that identity. Register first, then link your provider in Settings.
                      </p>
                    )}
                    <Button
                      type="submit"
                      disabled={loginLoading}
                      className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
                    >
                      {loginLoading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                  <SsoButtons intent="signin" returnTo="/" />
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle>Create Your Ranch</CardTitle>
                  <CardDescription>
                    Set up a new ranch and start managing your operation.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reg-name">Your Name</Label>
                      <Input
                        id="reg-name"
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        placeholder="John Rancher"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-email">Email</Label>
                      <Input
                        id="reg-email"
                        type="email"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        placeholder="you@ranch.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-password">Password</Label>
                      <Input
                        id="reg-password"
                        type="password"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        placeholder="••••••"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-ranch">Ranch Name</Label>
                      <Input
                        id="reg-ranch"
                        value={regRanch}
                        onChange={(e) => setRegRanch(e.target.value)}
                        placeholder="My Ranch"
                      />
                    </div>
                    {regError && (
                      <p className="text-sm text-destructive">{regError}</p>
                    )}
                    <Button
                      type="submit"
                      disabled={regLoading}
                      className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
                    >
                      {regLoading ? "Creating..." : "Create Ranch"}
                    </Button>
                  </form>
                  <SsoButtons intent="signup" returnTo="/" />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}